import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HiChevronDown,
  HiChevronRight,
  HiOutlineChatBubbleLeftRight,
  HiOutlineNewspaper,
  HiOutlineShieldCheck,
  HiOutlineSpeakerWave,
  HiOutlineSquaresPlus
} from 'react-icons/hi2';
import { ReviewIcon } from '../icons/ReviewIcon';
import { AgentIcon } from '../../agents/components/AgentIcon';
import { ProfileDropdown } from '../ui/ProfileDropdown';
import { useKodi } from '../../providers/KodiProvider';
import { useAudio } from '../../providers/AudioProvider';
import { useOrg } from '../../providers/OrgProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { useAgentEntitlements } from '../../hooks/useAgentEntitlements';
import { logger } from '../../../utils/logger';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  pathPattern?: string | string[]; // Substring pattern(s) matched against pathname in isActive
}

// Remembers the last-visited agent child so the subnav can highlight it as a
// "you were here" affordance while on the /v2/agents selector grid.
const AGENTS_LAST_SLUG_KEY = 'vb:agents:last-slug';

// sessionStorage can throw synchronously (SecurityError) when cookies are
// blocked or in strict privacy modes — and the read happens during render, so
// an unguarded call would crash the rail. The memory is a UX nicety, so treat
// storage as best-effort: warn once, then degrade to "no last-visited memory".
let storageWarned = false;
const warnStorageOnce = (err: unknown) => {
  if (!storageWarned) {
    storageWarned = true;
    logger.warn('[Sidebar] sessionStorage unavailable, agent subnav memory disabled:', err);
  }
};
const safeSessionGet = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch (err) {
    warnStorageOnce(err);
    return null;
  }
};
const safeSessionSet = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch (err) {
    warnStorageOnce(err);
  }
};

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // V2 org state (from OrgProvider)
  const { currentOrg } = useOrg();
  const org_id = currentOrg?.org_id;

  // Get Kodi state
  const { isProcessing, hasUnreadResponse } = useKodi();
  const { isLiveActive, hasBeenLive, volume } = useAudio();
  const { isAdmin } = usePermissions();
  const { isFeatureEntitled, entitledAgents } = useAgentEntitlements();

  // Build org-aware paths
  const feedPath = org_id ? `/v2/organizations/${org_id}/feed` : '/v2/feed';

  const navItems: NavItem[] = [
    ...(isFeatureEntitled('ask-kodi')
      ? [
          {
            id: 'assistant',
            label: 'Ask KODI',
            path: '/v2/assistant',
            icon: <HiOutlineChatBubbleLeftRight className="w-6 h-6" />
          }
        ]
      : []),
    {
      id: 'feed',
      label: 'Live',
      path: feedPath,
      // The Live page serves at both /feed and /channels (the latter is the
      // default landing), so both must mark the nav item active (#324).
      pathPattern: ['/feed', '/channels'],
      icon: <HiOutlineSpeakerWave className="w-6 h-6" />
    },
    ...(isFeatureEntitled('reviews')
      ? [
          {
            id: 'analytics',
            label: 'Review',
            path: '/v2/dashboard',
            icon: <ReviewIcon className="w-6 h-6" />
          }
        ]
      : []),
    {
      id: 'subs',
      label: 'Subs',
      path: '/v2/subs',
      icon: <HiOutlineNewspaper className="w-6 h-6" />
    },
    {
      // Alerts lives under Agents (registry entry `alerts`) — no top-level item.
      id: 'agents',
      label: 'Agents',
      path: '/v2/agents',
      icon: <HiOutlineSquaresPlus className="w-6 h-6" />
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin',
            path: '/v2/settings',
            icon: <HiOutlineShieldCheck className="w-6 h-6" />
          }
        ]
      : [])
  ];

  const isActive = (item: NavItem) => {
    const { path, pathPattern } = item;
    // Direct match or starts with path
    if (location.pathname === path || location.pathname.startsWith(path + '/')) {
      return true;
    }
    // Pattern match — any listed substring anywhere in the path
    if (pathPattern) {
      const patterns = Array.isArray(pathPattern) ? pathPattern : [pathPattern];
      if (patterns.some((p) => location.pathname.includes(p))) {
        return true;
      }
    }
    return false;
  };

  // Agents subnav — one entry per entitled registry item, styled like the main
  // nav (outline icon + label) but indented under Agents behind a tree-style
  // guide line (see agents-nav-group below). The active child's icon goes
  // primary-blue like any main nav item; on the selector grid (no child
  // active) the blue falls back to the most recently visited child as a
  // "you were here" cue.
  const activeAgentSlug = location.pathname.match(/^\/v2\/agents\/([^/]+)/)?.[1] ?? null;
  useEffect(() => {
    if (activeAgentSlug) {
      safeSessionSet(AGENTS_LAST_SLUG_KEY, activeAgentSlug);
    }
  }, [activeAgentSlug]);
  const boxedAgentSlug = activeAgentSlug ?? safeSessionGet(AGENTS_LAST_SLUG_KEY);

  return (
    <aside className="w-20 h-full bg-vb-surface border-r border-vb-border flex flex-col items-center py-6 dark:bg-vb-surface-dark dark:border-vb-border-dark">
      {/* Navigation Items */}
      <div className="flex flex-col items-center gap-2 w-full">
        {navItems.map((item) => {
          const active = isActive(item);
          const isKodi = item.id === 'assistant';
          const isFeed = item.id === 'feed';
          const showKodiIndicator = isKodi && (isProcessing || hasUnreadResponse);
          const kodiIndicatorColor = isProcessing ? 'bg-vb-primary' : 'bg-vb-danger';
          const showAudioDot = isFeed && isLiveActive && volume > 0;

          const showAgentsSubnav = item.id === 'agents' && active && entitledAgents.length > 0;

          const navButton = (
            <button
              onClick={() => navigate(item.path)}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
              className="w-full flex flex-col items-center justify-center gap-1 py-3 px-2 transition-all duration-200 hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark relative"
              title={item.label}
            >
              <span
                className={`relative ${
                  active
                    ? 'text-vb-primary'
                    : 'text-vb-text-secondary dark:text-vb-text-secondary-dark'
                }`}
              >
                {item.icon}
                {/* Agents gets an expand chevron: right when closed, down when
                    the subnav drawer is open. */}
                {item.id === 'agents' &&
                  (showAgentsSubnav ? (
                    <HiChevronDown
                      data-testid="agents-chevron-open"
                      className="absolute top-1/2 -right-4 -translate-y-1/2 w-3 h-3 text-vb-text-tertiary dark:text-vb-text-tertiary-dark"
                      aria-hidden="true"
                    />
                  ) : (
                    <HiChevronRight
                      data-testid="agents-chevron-closed"
                      className="absolute top-1/2 -right-4 -translate-y-1/2 w-3 h-3 text-vb-text-tertiary dark:text-vb-text-tertiary-dark"
                      aria-hidden="true"
                    />
                  ))}
                {showKodiIndicator && (
                  <span
                    className={`absolute -top-1 -right-1 w-3 h-3 ${kodiIndicatorColor} rounded-full animate-pulse`}
                  />
                )}
                {showAudioDot && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-vb-success rounded-full" />
                )}
              </span>
              <span className="text-xs font-medium text-vb-text-secondary dark:text-vb-text-secondary-dark normal-case">
                {item.label}
              </span>
            </button>
          );

          // While the Agents section is open, the children render beneath the
          // parent as a drawer bounded by hairlines.
          if (showAgentsSubnav) {
            return (
              <div key={item.id} data-testid="agents-nav-group" className="w-full">
                {navButton}
                {/* Hairline bounds above and below the child cluster — reads as
                    a drawer that slid open under Agents. Full rail width, no
                    indent; children center on the same axis as the main items. */}
                <div
                  role="group"
                  aria-label="Agents subnav"
                  className="w-full mb-2 flex flex-col gap-0.5 py-1 border-t border-b border-vb-border dark:border-vb-border-dark"
                >
                  {entitledAgents.map((agent) => {
                    const subActive = agent.slug === activeAgentSlug;
                    // Blue icon for the selected child (falls back to the most
                    // recently visited one on the selector grid) — same
                    // treatment as the main nav items.
                    const highlighted = agent.slug === boxedAgentSlug;
                    return (
                      <button
                        key={agent.slug}
                        onClick={() => navigate(`/v2/agents/${agent.slug}`)}
                        aria-label={agent.name}
                        aria-current={subActive ? 'page' : undefined}
                        title={agent.name}
                        className="w-full flex flex-col items-center gap-0.5 py-1.5 px-1 transition-colors hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark"
                      >
                        <span
                          className={
                            highlighted
                              ? 'text-vb-primary'
                              : 'text-vb-text-secondary dark:text-vb-text-secondary-dark'
                          }
                        >
                          <AgentIcon name={agent.icon} variant="outline" className="w-5 h-5" />
                        </span>
                        <span className="max-w-full truncate text-[10px] font-medium leading-tight normal-case text-vb-text-secondary dark:text-vb-text-secondary-dark">
                          {agent.navLabel ?? agent.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }

          return <React.Fragment key={item.id}>{navButton}</React.Fragment>;
        })}
      </div>

      {/* Bottom Section - Profile Dropdown (includes theme toggle) */}
      <div className="mt-auto flex flex-col items-center w-full">
        <ProfileDropdown position="sidebar" />
      </div>
    </aside>
  );
};
